/**
 * frontend/src/pages/valuation/ValuationTuning.tsx — REFACTOR-SPLIT VAL-TUNING-SPLIT-1
 * VALUATION-B3 — methodology tuning screen with live recompute; every change
 * re-runs POST .../recompute and .../preflight (same debounce). Preview
 * flags (out-of-range override comes back stated, flagged, band widened,
 * never clamped — CC#5), finalize fences (nothing is persisted;
 * `persisted: false` on every recompute; record created once, at sign-off).
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement } from '@/api/valuation'
import type { ValuationEngagement } from '@/types/valuation'
import { DriverQuestionnaireSection } from './_DriverQuestionnaire'
import { TuningControls } from './_TuningControls'
import { TuningSignoffColumn } from './_TuningSignoffColumn'
import { ValuationStepper, useValuationEntitlement } from './_shared'
import { buildRecomputeBody, type ResolvedIndustry } from './_valuationTuningHelpers'
import { benchmarkKeyForIndustry } from './_valuationIndustryMap'
import { useValuationDraft } from './_valuationDraft'
import { useTuningPreview } from './_useTuningPreview'
import { useTuningInputs } from './_useTuningInputs'

export default function ValuationTuning() {
  const { valuationId: id } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()
  const { canOverride } = useValuationEntitlement()

  const [loadError, setLoadError] = useState<string | null>(null)

  // VAL-NAV-STATE-1 R2 — industry is chosen once, at engagement creation
  // (ValuationProfileSelect), and displayed read-only here — never picked
  // on this screen. Fetched directly rather than read off the draft, so the
  // value shown is the engagement's own field, not whatever a prior Tuning
  // visit happened to save.
  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  useEffect(() => {
    if (!id) return
    let live = true
    getValuationEngagement(id)
      .then(en => { if (live) setEngagement(en) })
      .catch(() => { if (live) setLoadError('Could not load the engagement.') })
    return () => { live = false }
  }, [id])
  const resolvedIndustry: ResolvedIndustry | null = useMemo(
    () =>
      engagement
        ? { key: benchmarkKeyForIndustry(engagement.industry), naicsCode: null, subVertical: null }
        : null,
    [engagement],
  )

  // VAL-INTAKE-1 D2/D3/D4/D6 — the one working draft, shared with Intake.
  const {
    draft, draftTuning, draftExtras,
    resuming,
    resumedAtLabel,
    savedAtLabel,
    save: saveDraft,
    dismiss: dismissDraft,
  } = useValuationDraft(id)

  const {
    earnings, setEarnings,
    answers, setAnswers,
    knobs, resetKnobs,
    rationale, setRationale,
    acknowledgeMarketOnly, setAcknowledgeMarketOnly, clearMarketOnlyAck,
    config,
    questionnaire,
    baseline,
    tuned,
    startClean,
    onKnob,
  } = useTuningInputs(id, draft, dismissDraft, setLoadError, draftTuning)

  const earningsValue = Number(earnings)
  const earningsOk = earnings.trim() !== '' && Number.isFinite(earningsValue) && earningsValue > 0
  const answeredAll =
    !!questionnaire &&
    questionnaire.questions.filter(q => q.required).every(q => (answers[q.question_id] ?? '') !== '')
  const ready = !!resolvedIndustry && earningsOk && answeredAll

  /** The one body both /recompute and /preflight are called with. */
  const body = useMemo(
    () =>
      buildRecomputeBody({
        industry: resolvedIndustry,
        earningsOk,
        earningsValue,
        answers,
        knobs,
        baseline,
        tuned,
        rationale,
        draft,
        acknowledgeMarketOnly,
      }),
    [
      resolvedIndustry, earningsOk, earningsValue, answers, knobs, baseline, tuned, rationale,
      draft, acknowledgeMarketOnly,
    ],
  )

  // VAL-INTAKE-BLANK-JUDGMENT-1 C2 — any of the four saved rates blank.
  const ratesMissing = [
    draft?.risk_free_rate, draft?.company_specific_risk, draft?.long_term_growth_rate, draft?.growth_rate,
  ].some(v => v === null || v === undefined)

  // Debounced live recompute + preflight, and (D2) the settled-save trigger.
  const { computed, basis, blockers, warnings, runError, saveError, pending } = useTuningPreview(
    id,
    ready,
    body,
    saveDraft,
    ratesMissing,
  )

  // R3.11 — the working draft's save is now awaited inside useTuningPreview;
  // this is where the failure actually reaches the valuator, instead of
  // being lost in a fired-and-forgotten promise.
  useEffect(() => {
    if (saveError) toast.error(saveError)
  }, [saveError])

  // D6 — exact match on the engine's own wording (valuation_run.py); this is
  // the one case where "not triangulated" is a decision, not a footnote.
  const isMarketOnly = computed?.convergence_status === 'n/a — market approach only'
  useEffect(() => {
    if (!isMarketOnly) clearMarketOnlyAck()
  }, [isMarketOnly])

  if (loadError) {
    return (
      <div className="p-6">
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {loadError}
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <ValuationStepper current="tuning" engagementId={id} />

      <header className="mb-6">
        <h1 className="text-xl font-semibold text-[#0d1f3c]">Methodology & tuning</h1>
        <p className="mt-1 text-sm text-gray-600">
          Every number on the right came back from the engine on the current settings.
          Your inputs are saved as a working draft as you go — the signed-off record
          is still created once, at sign-off.
        </p>
        {resuming && (
          <p className="mt-2 flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <span>Resuming your draft from {resumedAtLabel}.</span>
            <button
              type="button"
              onClick={startClean}
              className="font-medium underline underline-offset-2 hover:text-blue-700"
            >
              Start clean
            </button>
          </p>
        )}
        {!resuming && savedAtLabel && (
          <p className="mt-2 text-xs text-gray-500">{savedAtLabel}</p>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Industry & earnings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className="mb-1 block text-sm font-medium">Industry</span>
                {engagement ? (
                  <p className="text-sm text-gray-700">{engagement.industry_label ?? engagement.industry ?? 'Not set'}</p>
                ) : (
                  <Skeleton className="h-4 w-32" />
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Set once at engagement creation — not editable here.
                </p>
              </div>
              <div>
                <label htmlFor="normalized-earnings" className="mb-1 block text-sm font-medium">
                  Normalized earnings
                </label>
                <Input
                  id="normalized-earnings"
                  inputMode="decimal"
                  value={earnings}
                  onChange={e => setEarnings(e.target.value)}
                  placeholder="620000"
                />
                <p className="mt-1 text-xs text-gray-500">
                  SDE or EBITDA — the engine picks the basis from the crossover
                  threshold below, and discloses which it used.
                </p>
              </div>
            </CardContent>
          </Card>

          {questionnaire && (
            <DriverQuestionnaireSection
              questionnaire={questionnaire}
              answers={answers}
              onAnswer={(qid, value) => setAnswers(prev => ({ ...prev, [qid]: value }))}
              persist={{ valuationId: id, saved: draftExtras.deferred_questions ?? undefined, enabled: !!draft }}
            />
          )}

          <Card>
            <CardHeader>
              <CardTitle>Tunable parameters</CardTitle>
            </CardHeader>
            <CardContent>
              {config && Object.keys(knobs).length > 0 ? (
                <TuningControls
                  values={knobs}
                  baseline={baseline}
                  marketWeight={knobs.market_weight ?? baseline.market_weight}
                  rationale={rationale}
                  tuned={tuned}
                  disabled={!canOverride}
                  onChange={onKnob}
                  onRationale={setRationale}
                  onReset={() => {
                    resetKnobs(baseline)
                    setRationale('')
                  }}
                />
              ) : (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="space-y-1">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ))}
                </div>
              )}
              {!canOverride && (
                <p className="mt-3 text-xs text-gray-500">
                  Your role can view the methodology but not tune it.
                </p>
              )}
            </CardContent>
          </Card>

          {/* QA-20 — one read-only line; no version list, no Activate. */}
        </div>

        <TuningSignoffColumn
          valueKind={engagement?.value_kind}
          computed={computed}
          basis={basis}
          pending={pending}
          runError={runError}
          blockers={blockers}
          warnings={warnings}
          isMarketOnly={isMarketOnly}
          acknowledgeMarketOnly={acknowledgeMarketOnly}
          onAcknowledgeMarketOnly={setAcknowledgeMarketOnly}
          onContinue={() => {
            if (!id) return navigate('/valuation')
            // Sign-off reads the saved draft (saved on every settled
            // recompute, D2); nothing is handed forward from here.
            navigate(`/valuation/${id}/signoff`)
          }}
        />
      </div>
    </div>
  )
}
